import { useState, useRef, useEffect } from 'react'
import Message from './Message'
import ThinkingIndicator from './ThinkingIndicator'
import { useChat } from '../hooks/useChat'
import './ChatInterface.css'

const WELCOME_MESSAGE = {
  id: 'welcome',
  role: 'assistant',
  content: `Welcome to Launch GPT by Represent Studio. 

We've helped 100+ startups launch to get real users, millions of views, and tens of millions in funding.

Now you have access to the same GPT that we’ve been using internally.

Let’s get started. Pick one of the following options: 

1. ⚡ **Quick** (2-3 min) — Paste your product URL, description or existing draft. I'll write the post.

2. 🏗️ **Full** (5-10 min) — Answer positioning questions for the best results.

Type **1** or **2**.
`
}

function ChatInterface() {
  const [inputValue, setInputValue] = useState('')
  const [showScrollButton, setShowScrollButton] = useState(false)
  const messagesEndRef = useRef(null)
  const messagesContainerRef = useRef(null)
  const textareaRef = useRef(null)
  const { messages, isLoading, error, sendMessage } = useChat()

  // Filter out empty assistant messages (they show ThinkingIndicator instead)
  const visibleMessages = messages.filter(msg => msg.role !== 'assistant' || msg.content)
  const allMessages = [WELCOME_MESSAGE, ...visibleMessages]

  // Check if user is at bottom of chat
  const checkIfAtBottom = () => {
    const container = messagesContainerRef.current
    if (!container) return true
    const threshold = 100
    return container.scrollHeight - container.scrollTop - container.clientHeight < threshold
  }

  // Handle scroll events to show/hide scroll button
  useEffect(() => {
    const container = messagesContainerRef.current
    if (!container) return

    const handleScroll = () => {
      setShowScrollButton(!checkIfAtBottom())
    }

    container.addEventListener('scroll', handleScroll)
    return () => container.removeEventListener('scroll', handleScroll)
  }, [])

  // Only auto-scroll when user sends a new message (not during streaming)
  useEffect(() => {
    // Only scroll to bottom when a new user message is added
    const lastMessage = messages[messages.length - 1]
    if (lastMessage?.role === 'user') {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
      setShowScrollButton(false)
    }
  }, [messages.length]) // Only trigger on message count change, not content updates

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
    setShowScrollButton(false)
  }

  // Auto-resize textarea
  useEffect(() => {
    const textarea = textareaRef.current
    if (textarea) {
      textarea.style.height = 'auto'
      textarea.style.height = `${Math.min(textarea.scrollHeight, 200)}px`
    }
  }, [inputValue])

  const handleSubmit = async (e) => {
    e.preventDefault()
    const trimmedValue = inputValue.trim()
    
    if (!trimmedValue || isLoading) return
    
    setInputValue('')
    // Reset textarea height
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto'
    }
    
    await sendMessage(trimmedValue)
  }

  const handleKeyDown = (e) => {
    // Submit on Enter (without Shift)
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSubmit(e)
    }
  }

  return (
    <div className="chat-interface">
      <div className="messages-container" ref={messagesContainerRef}>
        <div className="messages-list">
          {allMessages.map((message) => (
            <Message key={message.id} message={message} />
          ))}
          
          {isLoading && !messages[messages.length - 1]?.content && (
            <ThinkingIndicator />
          )}
          
          {error && (
            <div className="error-message">
              <svg viewBox="0 0 20 20" fill="currentColor" className="error-icon">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <span>{error}</span>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>
      </div>

      {showScrollButton && (
        <button 
          className="scroll-to-bottom"
          onClick={scrollToBottom}
          aria-label="Scroll to bottom"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </button>
      )}

      <form className="input-form" onSubmit={handleSubmit}>
        <div className="input-form-inner">
          <div className="input-container">
            <textarea
              ref={textareaRef}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Describe your product... What problem does it solve?"
              rows={1}
              className="message-input"
            />
            <button 
              type="submit" 
              className="send-button"
              disabled={!inputValue.trim() || isLoading}
              aria-label="Send message"
            >
              {isLoading ? (
                <svg className="loading-spinner" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeDasharray="31.4 31.4" />
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="22" y1="2" x2="11" y2="13" />
                  <polygon points="22 2 15 22 11 13 2 9 22 2" />
                </svg>
              )}
            </button>
          </div>
          <p className="input-hint">Press Enter to send, Shift+Enter for new line</p>
        </div>
      </form>
    </div>
  )
}

export default ChatInterface
